import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Fuel, Loader2 } from 'lucide-react';
import { Switch } from '../ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-admin/subscriber-gas`;
const eth = (v) => (v == null ? 'unavailable' : `${Number(v).toFixed(6)} ETH`);

const Row = ({ r }) => (
  <tr className="border-t border-slate-700" data-testid={`sub-gas-row-${r.client_id}`}>
    <td className="py-2 pr-3"><p className="text-white">{r.name || r.email}</p><p className="text-xs text-slate-500">{r.email}</p></td>
    <td className="py-2 pr-3 text-xs text-slate-400 font-mono">{r.wallet_address.slice(0, 10)}…{r.wallet_address.slice(-6)}</td>
    <td className={`py-2 text-right ${r.below_min ? 'text-amber-300' : 'text-emerald-400'}`} data-testid={`sub-gas-balance-${r.client_id}`}>{eth(r.base_eth)}</td>
  </tr>
);

export const SubscriberGasCard = ({ token }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const headers = { Authorization: `Bearer ${token}` };
  const load = useCallback(() => axios.get(API, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => { setD(r.data); setErr(''); }).catch(e => setErr(e.response?.data?.detail || 'Could not load subscriber gas status')), [token]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (enabled) => {
    try { await axios.put(`${API}/settings`, { enabled }, { headers }); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  const runNow = async () => {
    setBusy(true);
    try {
      const r = (await axios.post(`${API}/run`, {}, { headers })).data;
      toast.success(`Topped up ${r.topped_up.length} wallet(s)${r.skipped.length ? `, ${r.skipped.length} waiting on reserve` : ''}${r.errors.length ? `, ${r.errors.length} failed` : ''}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Top-up failed'); } finally { setBusy(false); }
  };
  if (err && !d) return <p className="bg-slate-800 border border-red-500/30 rounded-xl p-4 text-sm text-red-400" data-testid="sub-gas-error">{String(err)}</p>;
  if (!d) return null;
  const last = d.last_run;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4" data-testid="subscriber-gas-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2"><Fuel className="w-5 h-5 text-emerald-400" /> Subscriber Base Gas</h3>
          <p className="text-sm text-slate-400 mt-1">Every hour, each subscriber wallet is topped back up to {d.settings.min_eth} ETH on Base from the treasury reserve ({eth(d.reserve_eth)}).</p>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={d.settings.enabled} onCheckedChange={toggle} data-testid="sub-gas-enabled-switch" />
          <span className="text-xs text-slate-400">{d.settings.enabled ? 'Hourly on' : 'Paused'}</span>
        </div>
      </div>
      <table className="w-full text-sm"><tbody>{d.subscribers.map(r => <Row key={r.client_id} r={r} />)}</tbody></table>
      {!d.subscribers.length && <p className="text-sm text-slate-500" data-testid="sub-gas-empty">No subscribers yet.</p>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500" data-testid="sub-gas-last-run">{last ? `Last run ${new Date(last.at).toLocaleString()} · ${last.disabled ? 'paused' : `${last.topped_up?.length || 0} topped up`}` : 'Not run yet'}</p>
        <button onClick={runNow} disabled={busy} data-testid="sub-gas-run-btn" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm disabled:opacity-40 flex items-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Top up now
        </button>
      </div>
    </div>
  );
};
